import React, { useState } from 'react';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { PaperCard } from './PaperCard';
import { LoadingSkeleton } from './LoadingSkeleton';
import { usePapers } from '../hooks/usePapers';
import { usePersistence } from '../hooks/usePersistence';
import type { Paper, Topic } from '../types';
import { filterPapers } from '../utils/filtering';
import {
  Search,
  BookOpen,
  Filter,
  Loader2,
  Plus,
  X,
  Menu
} from 'lucide-react';

export function Dashboard() {
  // Persistence Hook
  const { topics, setTopics, bookmarks, setBookmarks } = usePersistence();

  // Primary State
  const [activeTopicName, setActiveTopicName] = useState<string>(topics[0]?.name || '');
  const [customTopic, setCustomTopic] = useState('');
  const [view, setView] = useState('feed'); // 'feed' or 'bookmarks'
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [filterQuery, setFilterQuery] = useState('');

  // Derived State
  const activeTopic = topics.find(t => t.name === activeTopicName) || topics[0] || { name: '', dateFrom: '', dateTo: '' };
  const { dateFrom, dateTo } = activeTopic;

  // Data Loading Hook
  const {
    papers,
    loading,
    hasMore,
    handleLoadMore,
  } = usePapers(activeTopicName, view, dateFrom, dateTo);

  // Per-Topic Date Update Handlers
  const updateTopicDates = (from: string, to: string) => {
    setTopics(prev => prev.map(t =>
      t.name === activeTopicName ? { ...t, dateFrom: from, dateTo: to } : t
    ));
  };

  // Handlers
  const handleAddTopic = (e: React.FormEvent) => {
    e.preventDefault();
    if (customTopic.trim() && !topics.some(t => t.name === customTopic)) {
      const newTopic: Topic = { name: customTopic, dateFrom: '', dateTo: '' };
      setTopics([newTopic, ...topics]);
      setActiveTopicName(customTopic);
      setView('feed');
      setCustomTopic('');
    }
  };

  const removeTopic = (topicToRemove: string) => {
    const newTopics = topics.filter(t => t.name !== topicToRemove);
    setTopics(newTopics);
    if (activeTopicName === topicToRemove && newTopics.length > 0) {
      setActiveTopicName(newTopics[0].name);
    }
  };

  const toggleBookmark = (paper: Paper) => {
    const exists = bookmarks.find(b => b.id === paper.id);
    if (exists) {
      setBookmarks(bookmarks.filter(b => b.id !== paper.id));
    } else {
      setBookmarks([paper, ...bookmarks]);
    }
  };

  // Local Filter Logic (Filter current view results)
  const filteredPapers = filterPapers(view === 'feed' ? papers : bookmarks, {
    dateFrom,
    dateTo,
    filterQuery
  });

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100 font-sans flex flex-col md:flex-row overflow-hidden">

      {/* Mobile Header */}
      <div className="md:hidden bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 p-4 flex items-center justify-between z-20 sticky top-0">
        <div className="flex items-center gap-2 font-bold text-indigo-600">
          <BookOpen size={24} />
          <span className="text-xl tracking-tight">Eolas Online</span>
        </div>
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2.5 text-slate-600 dark:text-slate-300 touch-manipulation">
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <Sidebar
        topics={topics}
        activeTopic={activeTopicName}
        setActiveTopic={setActiveTopicName}
        customTopic={customTopic}
        setCustomTopic={setCustomTopic}
        bookmarksCount={bookmarks.length}
        view={view}
        setView={setView}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        onAddTopic={handleAddTopic}
        onRemoveTopic={removeTopic}
      />

      {/* Main Content Area */}
      <main className="flex-1 h-screen overflow-y-auto bg-slate-50/50 dark:bg-slate-900/50 scroll-smooth">
        <div className="max-w-7xl mx-auto p-3 sm:p-4 md:p-6 lg:p-8">

          <Header
            view={view}
            activeTopic={activeTopicName}
            bookmarksCount={bookmarks.length}
            dateFrom={dateFrom}
            setDateFrom={(d) => updateTopicDates(d, dateTo)}
            dateTo={dateTo}
            setDateTo={(d) => updateTopicDates(dateFrom, d)}
            filterQuery={filterQuery}
            setFilterQuery={setFilterQuery}
          />

          {(loading && papers.length === 0) ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5 md:gap-6">
              {[1, 2, 3, 4, 5, 6].map(i => <LoadingSkeleton key={i} />)}
            </div>
          ) : (
            <>
              {view === 'feed' && papers.length === 0 && (
                <div className="flex flex-col items-center justify-center h-64 text-slate-400">
                  <Search size={48} className="mb-4 opacity-50" />
                  <p>No papers found for "{activeTopicName}". Try a different term.</p>
                </div>
              )}

              {filteredPapers.length === 0 && papers.length > 0 && (
                <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                  <Filter size={32} className="mb-3 opacity-50" />
                  <p>No matches for "{filterQuery}".</p>
                </div>
              )}

              {view === 'bookmarks' && bookmarks.length === 0 && (
                <div className="flex flex-col items-center justify-center h-64 text-slate-400 dark:text-slate-500 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-xl">
                  <BookOpen size={48} className="mb-4 opacity-50" />
                  <p>Your library is empty.</p>
                  <button
                    onClick={() => setView('feed')}
                    className="mt-4 text-indigo-600 font-medium hover:underline"
                  >
                    Browse feeds to add papers
                  </button>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5 md:gap-6">
                {filteredPapers.map((paper) => (
                  <PaperCard
                    key={paper.id}
                    paper={paper}
                    isBookmarked={bookmarks.some(b => b.id === paper.id)}
                    onToggleBookmark={toggleBookmark}
                  />
                ))}
              </div>

              {/* Load More Button */}
              {view === 'feed' && hasMore && papers.length > 0 && !filterQuery && (
                <div className="mt-8 flex justify-center pb-8">
                  <button
                    onClick={handleLoadMore}
                    disabled={loading}
                    className="flex items-center gap-2 px-5 sm:px-6 py-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 font-medium rounded-full shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 active:bg-slate-100 hover:text-indigo-600 dark:hover:text-indigo-400 transition-all disabled:opacity-50 touch-manipulation"
                  >
                    {loading ? <Loader2 className="animate-spin" size={20} /> : <Plus size={20} />}
                    {loading ? 'Loading...' : 'Load More Results'}
                  </button>
                </div>
              )}
            </>
          )}

          <footer className="mt-4 py-8 border-t border-slate-200 dark:border-slate-700 text-center text-slate-400 dark:text-slate-500 text-sm">
            <p>Data provided by <a href="https://openalex.org" target="_blank" rel="noreferrer" className="underline hover:text-indigo-500">OpenAlex</a>.</p>
          </footer>

        </div>
      </main>
    </div>
  );
}
